import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { usePlanFeature } from 'src/hooks/usePlanFeature';
import { openUpgradeDialog } from 'src/redux/slices/planSlice';

/**
 * PlanFeatureGuard — Route-level guard that blocks access when a plan feature is locked.
 *
 * Usage in routes:
 *   { path: 'somePath', element: <PlanFeatureGuard feature="REQUISITION_APPROVAL"><SomePage /></PlanFeatureGuard> }
 *
 * If the feature is locked, redirects to /dashboard and triggers the Upgrade Prompt Dialog.
 */
export default function PlanFeatureGuard({ feature, children, redirectTo = '/dashboard' }) {
  const dispatch = useDispatch();
  const { isLocked, requiredPlan, label, type, value } = usePlanFeature(feature);

  useEffect(() => {
    if (isLocked) {
      dispatch(
        openUpgradeDialog({
          requiredPlan,
          featureLabel: label || feature,
          featureType: type,
          limitValue: value,
        })
      );
    }
  }, [isLocked, feature, label, type, value, requiredPlan, dispatch]);

  if (isLocked) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
}

PlanFeatureGuard.propTypes = {
  feature: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  redirectTo: PropTypes.string,
};
